// The signed-in shell: header, section tabs, filter bar, widget palette + the
// canvas, and the Save button. Each section (Leagues / Players) is an
// independent canvas with its own saved layout and its own remembered scope
// (§7B decision 4) — switching tabs never bleeds one into the other.

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LAYOUT_SECTIONS, type LayoutSection, type LayoutBoard } from "@catbento/shared";
import { useAuth } from "../contexts/AuthContext.js";
import { useCanvasStore } from "../store/canvas.js";
import { toPlacements } from "../store/placements.js";
import { loadLayout, saveLayout } from "../data/layouts.js";
import { readInitialSection, persistSection } from "../lib/sectionScope.js";
import { useLeaguesScope } from "../filters/leaguesScope.js";
import { usePlayersScope } from "../filters/playersScope.js";
import type { WidgetScope } from "../widgets/scope.js";
import { theme } from "../lib/theme.js";
import FilterBar from "./FilterBar.js";
import WidgetPalette from "./WidgetPalette.js";
import Canvas from "./Canvas.js";

const SECTION_LABEL: Record<LayoutSection, string> = {
  leagues: "Leagues",
  players: "Players",
};

export default function CanvasShell() {
  const { user, signOut } = useAuth();
  const [section, setSection] = useState<LayoutSection>(readInitialSection);

  const switchSection = (next: LayoutSection) => {
    setSection(next);
    persistSection(next);
  };

  // Load this section's saved board once, then hydrate the store. staleTime
  // Infinity + the store's `loaded` guard mean switching tabs doesn't refetch or
  // clobber unsaved edits.
  const { data, isPending, isError, error } = useQuery<LayoutBoard>({
    queryKey: ["layout", section],
    queryFn: () => loadLayout(section),
    staleTime: Infinity,
  });
  const loaded = useCanvasStore((s) => s.loaded[section]);
  const hydrate = useCanvasStore((s) => s.hydrate);
  useEffect(() => {
    if (data && !loaded) hydrate(section, data.placements);
  }, [data, loaded, section, hydrate]);

  const placements = useCanvasStore((s) => s.placements[section]);
  const dirty = useCanvasStore((s) => s.dirty[section]);
  const markSaved = useCanvasStore((s) => s.markSaved);
  const reset = useCanvasStore((s) => s.reset);
  const qc = useQueryClient();

  // Sign out AND wipe the in-memory canvas + query cache — otherwise the
  // module-level store outlives the session and an unsaved canvas would
  // reappear on the next login (bug fixed in FE2c). The next login re-hydrates
  // from the backend, showing only the SAVED layout.
  const onSignOut = async () => {
    await signOut();
    reset();
    qc.clear();
  };

  // Each section's filter scope is independent (§7B). Leagues has League+Season;
  // Players (bio canary ignores it) gets an empty scope until FE5.
  const leaguesScope = useLeaguesScope();
  const playersScope = usePlayersScope();
  const scope: WidgetScope =
    section === "leagues"
      ? { competitionId: leaguesScope.competitionId, seasonId: leaguesScope.seasonId, playerId: null }
      : {
          competitionId: playersScope.competitionId,
          seasonId: playersScope.seasonId,
          playerId: playersScope.playerId,
        };

  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const onSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const board = await saveLayout(section, toPlacements(placements));
      qc.setQueryData(["layout", section], board);
      markSaved(section);
    } catch (e) {
      setSaveError(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: theme.page,
        fontFamily: "system-ui, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 20px",
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <span style={{ color: theme.ink, fontWeight: 700, fontSize: 18 }}>Catalyst</span>
        {/* Section tabs */}
        <nav style={{ display: "flex", gap: 6, marginLeft: 12 }}>
          {LAYOUT_SECTIONS.map((s) => (
            <button
              key={s}
              onClick={() => switchSection(s)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: `1px solid ${s === section ? theme.accent : theme.border}`,
                background: s === section ? theme.accentSurface : "transparent",
                color: s === section ? theme.accent : theme.inkMuted,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {SECTION_LABEL[s]}
            </button>
          ))}
        </nav>
        <span style={{ flex: 1 }} />
        <button
          onClick={() => void onSave()}
          disabled={!dirty || saving}
          style={{
            padding: "7px 16px",
            borderRadius: 8,
            border: "none",
            background: dirty && !saving ? theme.accent : theme.surface,
            color: dirty && !saving ? theme.page : theme.inkMuted,
            fontSize: 13,
            fontWeight: 600,
            cursor: dirty && !saving ? "pointer" : "default",
          }}
        >
          {saving ? "Saving…" : dirty ? "Save" : "Saved"}
        </button>
        {user && <span style={{ color: theme.inkMuted, fontSize: 13 }}>{user.email}</span>}
        <button
          onClick={() => void onSignOut()}
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            border: `1px solid ${theme.border}`,
            background: "transparent",
            color: theme.inkMuted,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Sign out
        </button>
      </header>

      {/* flex:1 + minHeight:0 so the canvas row can fill the viewport — the
          background grid then always spans the whole canvas, independent of
          widget size (FE2c-fix2). */}
      <main style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, flex: 1, minHeight: 0 }}>
        <FilterBar section={section} leaguesScope={leaguesScope} playersScope={playersScope} />
        {saveError && <div style={{ color: theme.danger, fontSize: 13 }}>Couldn't save. {saveError}</div>}
        {isError && (
          <div style={{ color: theme.danger, fontSize: 13 }}>Couldn't load your canvas. {String(error)}</div>
        )}
        <div style={{ display: "flex", gap: 14, alignItems: "stretch", flex: 1, minHeight: 0 }}>
          <WidgetPalette
            section={section}
            collapsed={paletteCollapsed}
            onToggle={() => setPaletteCollapsed((v) => !v)}
          />
          {isPending && !loaded ? (
            <div style={{ flex: 1, color: theme.inkMuted, fontSize: 14, padding: 20 }}>Loading your canvas…</div>
          ) : (
            <Canvas section={section} scope={scope} />
          )}
        </div>
      </main>
    </div>
  );
}
